import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { Device } from '@/models/device'
import { CustomField } from '@/models/customField'

type SortColumn = keyof Device | null
type SortDirection = 'asc' | 'desc'

interface DeviceTableHeaderProps {
  customFields: CustomField[],
  canEditDevices: boolean
  columnWidths: number[]
  sortColumn: SortColumn
  sortDirection: SortDirection
  allSelected: boolean
  onSort: (column: keyof Device) => void
  onSelectAll: () => void
  onResizeStart: (e: React.MouseEvent, index: number) => void
}

export default function DeviceTableHeader({
  customFields,
  canEditDevices,
  columnWidths,
  sortColumn,
  sortDirection,
  allSelected,
  onSort,
  onSelectAll,
  onResizeStart
}: DeviceTableHeaderProps) {

  const hasCustomFieldsPermission = authService.hasPermission(RESOURCES.CUSTOM_FIELDS, PermissionScope.READ);
  
  const getSortIcon = (column: keyof Device) => {
    if (sortColumn !== column) return '↕'
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  return (
    <thead>
      <tr>
        {canEditDevices && (
          <th
            className="checkbox-column resizable"
            title="Select devices for multi-edit"
            style={{ width: `${columnWidths[0]}px` }}
          >
            <span>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onSelectAll}
              />
              Multi-Edit
            </span>
            <div
              className="resize-handle"
              onMouseDown={(e) => onResizeStart(e, 0)}
            />
          </th>
        )}

        <th
          onClick={() => onSort('serialNumber')}
          className="sortable resizable"
          title="Device Serial Number"
          style={{ width: `${columnWidths[1]}px` }}
        >
          <span>Serial # {getSortIcon('serialNumber')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 1)}
          />
        </th>

        <th
          onClick={() => onSort('imei')}
          className="sortable resizable"
          title="IMEI Number"
          style={{ width: `${columnWidths[2]}px` }}
        >
          <span>IMEI {getSortIcon('imei')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 2)}
          />
        </th>

        <th
          onClick={() => onSort('type')}
          className="sortable resizable"
          title="Device Type"
          style={{ width: `${columnWidths[3]}px` }}
        >
          <span>Type {getSortIcon('type')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 3)}
          />
        </th>

        <th
          onClick={() => onSort('phoneNumber')}
          className="sortable resizable"
          title="Phone Number"
          style={{ width: `${columnWidths[4]}px` }}
        >
          <span>Phone {getSortIcon('phoneNumber')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 4)}
          />
        </th>

        <th
          onClick={() => onSort('simSerialNumber')}
          className="sortable resizable"
          title="SIM Serial Number"
          style={{ width: `${columnWidths[5]}px` }}
        >
          <span>SIM # {getSortIcon('simSerialNumber')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 5)}
          />
        </th>

        <th
          onClick={() => onSort('simProviderName')}
          className="sortable resizable"
          title="SIM Provider"
          style={{ width: `${columnWidths[6]}px` }}
        >
          <span>Provider {getSortIcon('simProviderName')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 6)}
          />
        </th>

        {customFields.map((field, index) => {
          const columnIndex = 7 + index; 
          const sortKey = `cf_${field.id}`;

          return (
            <th
              key={field.id}
              className="sortable resizable"
              title={field.name}
              style={{ width: `${columnWidths[columnIndex] || 150}px` }}
            >
              <span>
                {field.name}
              </span>
              <div
                className="resize-handle"
                onMouseDown={(e) => onResizeStart(e, columnIndex)}
              />
            </th>
          );
        })}

        <th
          onClick={() => onSort('firmware')}
          className="sortable resizable"
          title="Firmware Version"
          style={{ width: `${columnWidths[9]}px` }}
        >
          <span>Firmware {getSortIcon('firmware')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 9)}
          />
        </th>

        <th
          onClick={() => onSort('configurationId')}
          className="sortable resizable"
          title="Configuration Name"
          style={{ width: `${columnWidths[10]}px` }}
        >
          <span>Configuration {getSortIcon('configurationId')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 10)}
          />
        </th>

        <th
          onClick={() => onSort('dateAddedUtc')}
          className="sortable resizable"
          title="Date Added"
          style={{ width: `${columnWidths[11]}px` }}
        >
          <span>Added {getSortIcon('dateAddedUtc')}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 11)}
          />
        </th>

        <th
          className="resizable"
          title="Config Update Status"
          style={{ width: `${columnWidths[12]}px` }}
        >
          <span>Configuration Status</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 12)}
          />
        </th>

        <th
          className="resizable"
          title="Firmware Update Status"
          style={{ width: `${columnWidths[13]}px` }}
        >
          <span>Firmware Status</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => onResizeStart(e, 13)}
          />
        </th>

        {canEditDevices && (
          <th
            className="actions-column resizable"
            title="Available Actions"
            style={{ width: `${columnWidths[14]}px` }}
          >
            <span>Actions</span>
            <div
              className="resize-handle"
              onMouseDown={(e) => onResizeStart(e, 14)}
            />
          </th>
        )}
      </tr>
    </thead>
  )
}

export type { SortColumn, SortDirection }